import { useState, useCallback, useEffect } from 'react';
import { Alert, LTSSService, FinancialSummary } from '../types/ltss';
import {
  WebhookConfig,
  getWebhookConfig,
  saveWebhookConfig,
  buildWebhookPayload,
  sendWebhook,
  shouldTriggerWebhook,
} from '../utils/webhook';

interface UseWebhookReturn {
  config: WebhookConfig;
  isLoading: boolean;
  lastResult: { success: boolean; error?: string; timestamp?: Date } | null;
  updateConfig: (config: Partial<WebhookConfig>) => void;
  testWebhook: () => Promise<void>;
  triggerWebhook: (
    fileName: string,
    services: LTSSService[],
    alerts: Alert[],
    financials: FinancialSummary,
    staffCount: number,
    clientCount: number
  ) => Promise<void>;
}

export function useWebhook(): UseWebhookReturn {
  const [config, setConfig] = useState<WebhookConfig>(getWebhookConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<{ 
    success: boolean; 
    error?: string; 
    timestamp?: Date 
  } | null>(null);

  // Load config on mount
  useEffect(() => {
    setConfig(getWebhookConfig());
  }, []);

  const updateConfig = useCallback((updates: Partial<WebhookConfig>) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      saveWebhookConfig(newConfig);
      return newConfig;
    });
  }, []);

  const testWebhook = useCallback(async () => {
    if (!config.url) {
      setLastResult({ success: false, error: 'No webhook URL configured' });
      return;
    }

    setIsLoading(true);
    
    const testPayload = {
      timestamp: new Date().toISOString(),
      source: 'ltss-analytics-dashboard' as const,
      event: 'alerts_detected' as const,
      fileName: 'test-file.xlsx',
      summary: {
        totalServices: 100,
        totalAlerts: 5,
        criticalAlerts: 2,
        warningAlerts: 3,
        totalRevenue: 15000,
        staffCount: 10,
        clientCount: 25,
      },
      alerts: [
        {
          id: 'test-1',
          type: 'CRITICAL',
          category: 'Missing Time',
          message: 'Test alert: Missing time for John Doe on 12/01/2025',
          staffName: 'John Doe',
          clientName: 'Jane Smith',
        },
      ],
      metadata: {
        weekRange: '12/01/2025 - 12/07/2025',
        uploadDate: new Date().toISOString(),
      },
    };

    const result = await sendWebhook(testPayload, config.url);
    setLastResult({ ...result, timestamp: new Date() });
    setIsLoading(false);
  }, [config.url]);

  const triggerWebhook = useCallback(async (
    fileName: string,
    services: LTSSService[],
    alerts: Alert[],
    financials: FinancialSummary,
    staffCount: number,
    clientCount: number
  ) => {
    if (!shouldTriggerWebhook(alerts, config)) {
      return;
    }

    setIsLoading(true);
    
    const payload = buildWebhookPayload(
      fileName,
      services,
      alerts,
      financials,
      staffCount,
      clientCount,
      config
    );

    const result = await sendWebhook(payload, config.url);
    
    if (result.success) {
      updateConfig({ lastTriggered: new Date() });
    }
    
    setLastResult({ ...result, timestamp: new Date() });
    setIsLoading(false);
  }, [config, updateConfig]);

  return {
    config,
    isLoading,
    lastResult,
    updateConfig,
    testWebhook,
    triggerWebhook,
  };
}
